import {Button as AntButton} from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import {useMemo} from 'react'

import {CheckCircleFilledIcon, InProgressIcon, LiveIcon} from '../../icons'
import theme from '../../theme'
import {FlexCol, FlexRow} from '../../utils/layout'
import {
  ActiveBadge,
  Badge,
  CardContainer,
  CardContainerProps,
  LiveIndicatorWrapper,
  RightSideIconWrapper,
  StyledUserIcon,
  TextInfoBold,
  TextInfoLight,
} from './styles'

const LiveIndicator = ({children}: { children: React.ReactElement }) => {
  return (
    <LiveIndicatorWrapper>
      {children}
      <LiveIcon title="Active procedure icon"/>
      <div className="pulse-1"></div>
      <div className="pulse-2"></div>
    </LiveIndicatorWrapper>
  )
}

const StatusIcon = ({icon}: Pick<ProcedureCardProps, 'icon'>) => {
  switch (icon) {
    case 'InProgress':
      return <InProgressIcon style={{color: theme.colors.gray6}}/>
    case 'CheckCircleFilled':
      return <CheckCircleFilledIcon style={{color: theme.colors.gray6}}/>
    case undefined:
      return null
    default:
      return icon
  }
}

export interface ProcedureCardStylesProps extends CardContainerProps {
  primaryButtonType: 'default' | 'link' | 'text' | 'primary' | 'dashed' | 'outlined'
  secondaryButtonType: 'default' | 'link' | 'text' | 'primary' | 'dashed' | 'outlined'
}

export interface ProcedureCardProps {
  name: string
  birthdate: string
  doctor: string
  type?: string
  primaryButtonText?: string
  secondaryButtonText?: string
  label?: string
  handlePrimaryButtonClick: () => void
  handleSecondaryButtonClick?: () => void
  isActive?: boolean
  icon?: 'InProgress' | 'CheckCircleFilled' | JSX.Element
  cardStyles?: ProcedureCardStylesProps
  primaryButtonDisabled?: boolean
  secondaryButtonDisabled?: boolean
}

const ProcedureCard = ({
                         name,
                         birthdate,
                         doctor,
                         type,
                         primaryButtonText,
                         secondaryButtonText,
                         label,
                         handlePrimaryButtonClick,
                         handleSecondaryButtonClick,
                         icon,
                         cardStyles,
                         isActive,
                         primaryButtonDisabled,
                         secondaryButtonDisabled,
                       }: ProcedureCardProps) => {
  const nameInitials = useMemo(() => {
    if (name && name.indexOf(' ') > 0) {
      const nameParts = name.split(' ')
      return `${nameParts[0][0]}${nameParts[1][0]}`
    }
    return name[0]
  }, [name])

  const birthdateFormatted = useMemo(() => dayjs(new Date(birthdate)).format('DD.MM.YYYY'), [birthdate])

  return (
    <CardContainer id={`nova-cardContainer-${name.replaceAll(' ', '-')}`} {...cardStyles}>
      <FlexRow justify="flex-start" align="center">
        {isActive ? (
          <LiveIndicator>
            <ActiveBadge>{nameInitials}</ActiveBadge>
          </LiveIndicator>
        ) : (
          <Badge>{nameInitials}</Badge>
        )}
        <FlexCol justify="center" align="flex-start" margin="0 0 0 12px">
          <TextInfoBold>{name}</TextInfoBold>
          <FlexRow justify="flex-start" align="center">
            <TextInfoLight>{birthdateFormatted}</TextInfoLight>
            <StyledUserIcon/>
            <TextInfoLight>{doctor}</TextInfoLight>
            {type && <TextInfoLight margin={'0 0 0 18px'}>{type}</TextInfoLight>}
          </FlexRow>
        </FlexCol>
      </FlexRow>

      <FlexRow>
        <RightSideIconWrapper>
          <StatusIcon icon={icon}/>
        </RightSideIconWrapper>
        {label && <TextInfoLight margin="0 12px 0 0"> {label}</TextInfoLight>}
        {secondaryButtonText && (
          <AntButton
            type={cardStyles?.secondaryButtonType !== 'outlined' ? cardStyles?.secondaryButtonType : 'default'}
            ghost={cardStyles?.secondaryButtonType === 'outlined'}
            disabled={secondaryButtonDisabled}
            onClick={handleSecondaryButtonClick}
            data-cy="procedure-card-cta-button"
            id="procedure-card-cta-button"
            style={{margin: '0 12px 0 0'}}
          >
            {secondaryButtonText}
          </AntButton>
        )}
        {primaryButtonText && (
          <AntButton
            id={`nova-addFindingsButton`}
            type={cardStyles?.primaryButtonType !== 'outlined' ? cardStyles?.primaryButtonType : 'primary'}
            ghost={cardStyles?.primaryButtonType === 'outlined'}
            disabled={primaryButtonDisabled}
            onClick={handlePrimaryButtonClick}
            data-cy="procedure-card-cta-button"
          >
            {primaryButtonText}
          </AntButton>
        )}
      </FlexRow>
    </CardContainer>
  )
}

export default ProcedureCard
